import { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useEmployerProgramsContext } from 'Contexts/EmployerProgramsContext'
import { FlexColumn, FlexRow, noPadding, spacingMD, spacingSM } from 'Utils/styles/spacing'
import { JoonAdminProgramDetailsCard } from 'Pages/JOONAdmin/EmployerDetailsPage/Views/BenefitPrograms/JoonAdminProgramDetailsCard'
import { TableLabel } from 'Utils/styles/text'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { StyledSelect } from 'Components/Inputs/StyledSelect'
import { CreateBenefitProgramModal } from 'Pages/JOONAdmin/EmployerDetailsPage/Views/BenefitPrograms/CreateBenefitProgramModal/CreateBenefitProgramModal'
import { EmployerBenefitProgram } from 'Utils/types/benefitProgram'
import { Checkbox } from 'Components/Inputs/Checkbox/Checkbox'
import { LocalStorageKeys, useLocalStorage } from 'Hooks/useLocalStorage'
import { useCountries } from 'Hooks/useCountries'
import { isBenefitProgramActive } from 'Utils/helpers/programUsage'
import { CountryCode } from 'Utils/types/countries'

export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr); // Three equally spaced columns
  gap: ${spacingSM};
`

export const JoonAdminBenefitProgramsGrid: FC = () => {
  const {
    state: { allPrograms }
  } = useEmployerProgramsContext()
  const [hideInactivePrograms, setHideInactivePrograms] = useLocalStorage(
    LocalStorageKeys.HideInactivePrograms,
    false
  )
  const [countryCode, setCountryCode] = useState<CountryCode | ''>('')
  const { hasMultipleCountries, countryOptionsWithAll } = useCountries()

  const filteredPrograms = useMemo(() => {
    return allPrograms.filter(
      (program) =>
        (!hideInactivePrograms || isBenefitProgramActive(program)) &&
        (!countryCode || program.countries.includes(countryCode))
    )
  }, [allPrograms, hideInactivePrograms, countryCode])

  const [selectedProgram, setSelectedProgram] = useState<EmployerBenefitProgram | undefined>(
    undefined
  )

  const [showCreateProgramModal, setShowCreateProgramModal] = useState(false)
  const setShowModal = (value: boolean) => {
    setShowCreateProgramModal(value)
    if (!value) {
      setSelectedProgram(undefined)
    }
  }

  return (
    <>
      <FlexColumn gap={spacingSM}>
        <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <TableLabel marginBottom={noPadding}>Benefit Programs</TableLabel>
          <FlexRow
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
            gap={spacingMD}
          >
            {hasMultipleCountries && (
              <StyledSelect
                handleChange={setCountryCode}
                options={countryOptionsWithAll}
                defaultValue={countryCode}
              />
            )}
            <Checkbox
              labelText="Hide Inactive Programs"
              value={hideInactivePrograms}
              onClick={() => setHideInactivePrograms((val) => !val)}
            />
            <StyledButton
              id="createBenefitProgram.button.label"
              primary
              onClick={() => {
                setSelectedProgram(undefined)
                setShowModal(true)
              }}
            />
          </FlexRow>
        </FlexRow>
        <GridContainer>
          {filteredPrograms.map((program) => (
            <JoonAdminProgramDetailsCard
              key={program.id}
              program={program}
              onClick={() => {
                setSelectedProgram(program)
                setShowModal(true)
              }}
            />
          ))}
        </GridContainer>
      </FlexColumn>
      <CreateBenefitProgramModal
        showModal={showCreateProgramModal}
        setShowModal={setShowModal}
        selectedProgram={selectedProgram}
      />
    </>
  )
}
